import { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash.debounce';
import Popup from 'utils/popup';
import {
  ActivityIndicator,
  Avatar,
  Button,
  Field,
  Select,
  TextInput,
} from '@kontentino/ui';
import { useToast } from 'app/hooks/useToast';
import usePermissionsKeys from 'app/hooks/usePermissionsKeys';
import { hasPermission } from '@kontentino/react-permissions-gate';
import { UserPermission } from 'constants/userPermission';
import EmptyState from 'app/components/EmptyState';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { applyLaravelErrors } from 'utils/laravelValidationErrors';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'components/shared/sheet';
import PageApi from 'api/page';
import ProfilesSelector from 'app/components/ProfilesSelector';
import { togglePrimitiveItemInArray } from 'utils/helpers';
import { PageData } from 'types/Page';
import { UsersApi } from 'app/modules/settings/modules/users/api/users';
import { UserListItem } from 'app/modules/settings/modules/users/types/user';
import LogoUploadField, {
  LOGO_MIME_TYPES,
} from 'app/modules/settings/components/LogoUploadField';
import { useUser } from 'modules/user/userSelector';
import { ProfileGroupSharedUser } from '../types/profileGroup';
import { useProfileGroupDetailQuery } from '../hooks/useProfileGroupDetailQuery';
import { useUpsertProfileGroupMutation } from '../hooks/useUpsertProfileGroupMutation';
import { ProfileGroupsApi } from '../api/profileGroups';

export const createEditProfileGroupSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().trim().min(1, t('validation.fieldRequired')).max(255),
    profile_ids: z.array(z.number()).min(1),
    user_ids: z.array(z.number()),
  });

type EditProfileGroupFormValues = z.infer<
  ReturnType<typeof createEditProfileGroupSchema>
>;

export type ProfileGroupSheetSection = 'profiles' | 'users';

interface UserOption {
  value: number;
  label: string;
  avatarSrc: string | null;
}

interface EditProfileGroupSheetProps {
  groupId: number | null;
  isOpen: boolean;
  onClose: () => void;
  focusSection?: ProfileGroupSheetSection;
  readOnly?: boolean;
}

const toUserOption = (user: ProfileGroupSharedUser): UserOption => ({
  value: user.id,
  label: user.name,
  avatarSrc: user.avatar?.src ?? null,
});

const SheetBodySkeleton = () => (
  <div
    className="tw-flex tw-flex-col tw-gap-4"
    data-cy="edit-profile-group-sheet_loading"
  >
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="tw-flex tw-flex-col tw-gap-2">
        <Skeleton width={120} height={14} />
        <Skeleton height={38} />
      </div>
    ))}
  </div>
);

const UserOptionLabel = ({ option }: { option: UserOption }) => (
  <div className="tw-flex tw-items-center tw-gap-2">
    <Avatar size={20} src={option.avatarSrc ?? undefined} name={option.label} />
    <span className="tw-truncate">{option.label}</span>
  </div>
);

interface SelectAllHeaderProps {
  onSelectAll: () => void;
  onClearAll: () => void;
  isDisabled?: boolean;
}

const SelectAllHeader = ({
  onSelectAll,
  onClearAll,
  isDisabled,
}: SelectAllHeaderProps) => {
  const { t } = useTranslation();
  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-grayscale-20 tw-px-3 tw-py-2">
      <button
        type="button"
        className="tw-text-xs tw-text-primary-100 hover:tw-underline disabled:tw-opacity-50"
        onClick={onSelectAll}
        disabled={isDisabled}
      >
        {t('profileGroupsManagement.modal.selectAll')}
      </button>
      <span className="tw-text-grayscale-60">·</span>
      <button
        type="button"
        className="tw-text-xs tw-text-primary-100 hover:tw-underline disabled:tw-opacity-50"
        onClick={onClearAll}
        disabled={isDisabled}
      >
        {t('profileGroupsManagement.modal.clearAll')}
      </button>
    </div>
  );
};

const EditProfileGroupSheet = ({
  groupId,
  isOpen,
  onClose,
  focusSection,
  readOnly = false,
}: EditProfileGroupSheetProps) => {
  const { t } = useTranslation();
  const { id: currentUserId } = useUser();
  const toast = useToast();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isLogoRemovalQueued, setIsLogoRemovalQueued] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const isEditing = groupId !== null;
  const groupQuery = useProfileGroupDetailQuery(isOpen ? groupId : null);
  const group = groupQuery.profileGroup ?? null;
  const isGroupUnavailable = isEditing && group === null;
  const canShare = hasPermission(
    usePermissionsKeys(),
    UserPermission.PAGE_PRESETS_SHARE,
  );
  const [userSearch, setUserSearch] = useState('');
  const [pickedUsers, setPickedUsers] = useState<Record<number, UserOption>>(
    {},
  );
  const debouncedSetUserSearch = useMemo(
    () => _debounce(setUserSearch, 300),
    [],
  );

  useEffect(
    () => () => debouncedSetUserSearch.cancel(),
    [debouncedSetUserSearch],
  );

  useEffect(() => {
    if (!isOpen || !focusSection) return;
    const target = formRef.current?.querySelector<HTMLElement>(
      `[data-section="${focusSection}"]`,
    );
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isOpen, focusSection, group?.id]);

  const invalidateGroup = (savedGroupId: number) => {
    queryClient.invalidateQueries(queryKey.profileGroupDetail(savedGroupId));
    queryClient.invalidateQueries(queryKey.profileGroupsList());
  };
  const uploadLogoAfterSave = useMutation(
    ({ groupId, file }: { groupId: number; file: File }) =>
      ProfileGroupsApi.uploadLogo(groupId, file),
    { onSuccess: (_data, { groupId }) => invalidateGroup(groupId) },
  );
  const removeLogoAfterSave = useMutation(
    (savedGroupId: number) => ProfileGroupsApi.removeLogo(savedGroupId),
    { onSuccess: (_data, savedGroupId) => invalidateGroup(savedGroupId) },
  );

  const upsertMutation = useUpsertProfileGroupMutation();

  const profilesQuery = useQuery(
    ['settings', 'profileGroups', 'profilesOptions'],
    () => PageApi.getAll(),
    { enabled: isOpen, staleTime: 60000 },
  );

  const usersQuery = useQuery(
    ['settings', 'profileGroups', 'usersOptions', userSearch],
    () => UsersApi.getList({ per_page: 100, search: userSearch || undefined }),
    {
      enabled: isOpen && canShare && !readOnly,
      staleTime: 60000,
      keepPreviousData: true,
    },
  );

  const workspaceProfiles: PageData[] = profilesQuery.data?.data ?? [];
  const workspaceProfileIds = new Set(
    workspaceProfiles.map((profile) => profile.id),
  );
  const selectableProfiles = [
    ...workspaceProfiles,
    ...(group?.profiles ?? [])
      .filter((profile) => !workspaceProfileIds.has(profile.id))
      .map((profile) => ({ ...profile, logo: null })),
  ];
  const isProfilesSelectionLocked = readOnly || upsertMutation.isLoading;

  // Like legacy, you never share a group with yourself.
  const userOptions: UserOption[] =
    usersQuery.data?.data
      ?.filter((user: UserListItem) => user.id !== currentUserId)
      .map((user: UserListItem) => ({
        value: user.id,
        label: user.full_name?.trim() || user.email,
        avatarSrc: null,
      })) ?? [];

  const knownUsers: Record<number, UserOption> = {
    ...Object.fromEntries(
      (group?.shared_with ?? []).map((user) => [user.id, toUserOption(user)]),
    ),
    ...pickedUsers,
  };

  const rememberUsers = (options: readonly UserOption[]) =>
    setPickedUsers((previous) => ({
      ...previous,
      ...Object.fromEntries(options.map((option) => [option.value, option])),
    }));

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm<EditProfileGroupFormValues>({
    defaultValues: {
      name: group?.name ?? '',
      profile_ids: group?.profiles.map((p) => p.id) ?? [],
      user_ids: group?.shared_with.map((u) => u.id) ?? [],
    },
    resolver: zodResolver(createEditProfileGroupSchema(t)),
    mode: 'onChange',
  });

  const hydratedGroupIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) {
      hydratedGroupIdRef.current = undefined;
      return;
    }
    if (isGroupUnavailable) return;

    const formGroupId = group?.id ?? null;
    // A refetch of the same group (e.g. after a logo upload) must not wipe unsaved edits.
    if (hydratedGroupIdRef.current === formGroupId) return;
    hydratedGroupIdRef.current = formGroupId;

    reset({
      name: group?.name ?? '',
      profile_ids: group?.profiles.map((p) => p.id) ?? [],
      user_ids: group?.shared_with.map((u) => u.id) ?? [],
    });
    setLogoFile(null);
    setIsLogoRemovalQueued(false);
    setLogoError(null);
    setPickedUsers({});
  }, [group, isGroupUnavailable, isOpen, reset]);

  const localLogoUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile],
  );

  useEffect(() => {
    return () => {
      if (localLogoUrl) URL.revokeObjectURL(localLogoUrl);
    };
  }, [localLogoUrl]);

  const logoPreviewUrl =
    localLogoUrl ?? (isLogoRemovalQueued ? null : group?.logo?.src ?? null);

  const isLogoBusy =
    uploadLogoAfterSave.isLoading ||
    removeLogoAfterSave.isLoading ||
    upsertMutation.isLoading;

  const handleLogoPick = (file: File) => {
    if (!LOGO_MIME_TYPES.includes(file.type)) {
      setLogoError(t('profilesManagement.logoUploadHint'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError(t('profileGroupsManagement.logoTooLarge'));
      return;
    }
    setLogoError(null);
    setLogoFile(file);
    setIsLogoRemovalQueued(false);
  };

  const handleLogoRemove = () => {
    if (logoFile) {
      setLogoFile(null);
      return;
    }
    if (group?.logo) setIsLogoRemovalQueued(true);
  };

  const hasUnsavedChanges =
    !readOnly && (isDirty || logoFile !== null || isLogoRemovalQueued);

  const handleGuardedClose = async () => {
    if (upsertMutation.isLoading) return;
    if (hasUnsavedChanges) {
      const { isConfirmed } = await Popup.confirm({
        title: t('settings.discardChangesTitle'),
        text: t('settings.discardChangesBody'),
        confirmButtonText: t('settings.discardChanges'),
      });
      if (!isConfirmed) return;
    }
    onClose();
  };

  const onSubmit = async (data: EditProfileGroupFormValues) => {
    if (readOnly) return;
    let savedGroup: { id: number } | undefined;
    try {
      savedGroup = await upsertMutation.mutateAsync({
        id: groupId ?? undefined,
        data: {
          name: data.name,
          profile_ids: data.profile_ids,
          ...(canShare && { user_ids: data.user_ids }),
        },
      });
    } catch (e) {
      if (!applyLaravelErrors(e, setError, { name: 'name' })) {
        toast(
          (e as ApiClientError)?.userMessage ?? t('somethingWentWrong'),
          'error',
        );
      }
      return;
    }
    const savedGroupId = groupId ?? savedGroup?.id;
    if (savedGroupId !== null && savedGroupId !== undefined) {
      try {
        if (logoFile) {
          await uploadLogoAfterSave.mutateAsync({
            groupId: savedGroupId,
            file: logoFile,
          });
        } else if (isLogoRemovalQueued) {
          await removeLogoAfterSave.mutateAsync(savedGroupId);
        }
      } catch {
        toast(t('profileGroupsManagement.logoUploadFailed'), 'warning');
      }
    }
    onClose();
  };

  const title = isEditing
    ? readOnly
      ? t('profileGroupsManagement.modal.viewTitle')
      : t('profileGroupsManagement.modal.editTitle')
    : t('profileGroupsManagement.modal.createTitle');

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleGuardedClose();
      }}
    >
      <SheetContent
        width="default"
        data-cy={
          isEditing ? 'edit-profile-group-sheet' : 'new-profile-group-sheet'
        }
        closeOnOverlayClick
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <SheetBody>
          {isEditing && groupQuery.isError ? (
            <EmptyState
              icon={faCircleExclamation}
              title={t('somethingWentWrong')}
              description={t('clickToRetry')}
              action={{
                label: t('retry'),
                onClick: () => groupQuery.refetch(),
                variant: 'secondary',
                dataCy: 'edit-profile-group-sheet_retry',
              }}
              dataCy="edit-profile-group-sheet_load-error"
            />
          ) : isGroupUnavailable ? (
            <SheetBodySkeleton />
          ) : (
            <form
              id="edit-profile-group-form"
              ref={formRef}
              onSubmit={handleSubmit(onSubmit)}
              className="tw-flex tw-flex-col tw-gap-4"
              data-focus-section={focusSection}
            >
              <Field.Group>
                <Field.Label required>
                  {t('profileGroupsManagement.modal.name')}
                </Field.Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextInput
                      {...field}
                      error={!!error?.message}
                      maxLength={255}
                      disabled={readOnly || upsertMutation.isLoading}
                      autoComplete="off"
                      data-1p-ignore
                      data-cy="profile-groups-management_edit-name-input"
                    />
                  )}
                />
                <Field.Error>{errors.name?.message}</Field.Error>
              </Field.Group>

              <Field.Group>
                <Field.Label>
                  {t('profileGroupsManagement.modal.logo')}
                </Field.Label>
                <LogoUploadField
                  name={group?.name ?? ''}
                  previewUrl={logoPreviewUrl}
                  onFilePick={handleLogoPick}
                  onRemove={handleLogoRemove}
                  canRemove={
                    !!(logoFile || (group?.logo && !isLogoRemovalQueued))
                  }
                  isBusy={isLogoBusy}
                  isReadOnly={readOnly}
                  hint={t('profileGroupsManagement.modal.logoHint')}
                  dataCy="profile-groups-management_logo-uploader"
                />
                {logoError && <Field.Error>{logoError}</Field.Error>}
              </Field.Group>

              <Field.Group data-section="profiles">
                <Field.Label required>
                  {t('profileGroupsManagement.modal.profiles')}
                </Field.Label>
                <Controller
                  name="profile_ids"
                  control={control}
                  render={({ field: { onChange, value } }) =>
                    profilesQuery.isLoading ? (
                      <ActivityIndicator className="tw-mx-auto tw-my-4" />
                    ) : (
                      <ProfilesSelector
                        profiles={selectableProfiles}
                        selectedIds={value}
                        onSelect={(id: number) =>
                          onChange(togglePrimitiveItemInArray(id, value))
                        }
                        onToggleAllSelection={
                          isProfilesSelectionLocked
                            ? undefined
                            : (selection) =>
                                onChange(
                                  selection === 'all'
                                    ? Array.from(
                                        new Set([
                                          ...value,
                                          ...selectableProfiles.map(
                                            (profile) => profile.id,
                                          ),
                                        ]),
                                      )
                                    : [],
                                )
                        }
                        disabledIds={
                          isProfilesSelectionLocked
                            ? selectableProfiles.map((profile) => profile.id)
                            : undefined
                        }
                      />
                    )
                  }
                />
              </Field.Group>

              <Field.Group data-section="users">
                <Field.Label>
                  {t('profileGroupsManagement.modal.users')}
                </Field.Label>
                <Controller
                  name="user_ids"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const selected = value.map(
                      (id) =>
                        knownUsers[id] ?? {
                          value: id,
                          label: String(id),
                          avatarSrc: null,
                        },
                    );
                    return (
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        value={selected}
                        options={userOptions}
                        onChange={(opts) => {
                          const picked = (opts as UserOption[]) ?? [];
                          rememberUsers(picked);
                          onChange(picked.map((o) => o.value));
                        }}
                        onInputChange={(input, { action }) => {
                          if (action === 'input-change') {
                            debouncedSetUserSearch(input);
                          }
                        }}
                        filterOption={null}
                        isLoading={usersQuery.isFetching}
                        getOptionValue={(option: UserOption) =>
                          String(option.value)
                        }
                        getOptionLabel={(option: UserOption) => option.label}
                        formatOptionLabel={(option: UserOption) => (
                          <UserOptionLabel option={option} />
                        )}
                        components={{
                          MenuList: ({ children, innerRef, innerProps }) => (
                            <div>
                              {!readOnly && !upsertMutation.isLoading && (
                                <SelectAllHeader
                                  onSelectAll={() => {
                                    rememberUsers(userOptions);
                                    onChange(
                                      Array.from(
                                        new Set([
                                          ...value,
                                          ...userOptions.map((o) => o.value),
                                        ]),
                                      ),
                                    );
                                  }}
                                  onClearAll={() => onChange([])}
                                />
                              )}
                              <div
                                ref={innerRef}
                                {...innerProps}
                                style={{
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                }}
                              >
                                {children}
                              </div>
                            </div>
                          ),
                        }}
                        isDisabled={
                          readOnly || !canShare || upsertMutation.isLoading
                        }
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (styles) => ({ ...styles, zIndex: 1010 }),
                        }}
                        data-cy="profile-groups-management_users-select"
                      />
                    );
                  }}
                />
              </Field.Group>
            </form>
          )}
        </SheetBody>
        <SheetFooter>
          <Button
            variant="secondary"
            onClick={handleGuardedClose}
            disabled={upsertMutation.isLoading}
          >
            {t(readOnly ? 'close' : 'cancel')}
          </Button>
          {!readOnly && (
            <Button
              type="submit"
              form="edit-profile-group-form"
              isLoading={upsertMutation.isLoading}
              disabled={!isValid || isGroupUnavailable}
              data-cy="profile-groups-management_submit"
            >
              {t('save')}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default EditProfileGroupSheet;
